import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * Product Engineer — one of the five open roles the Careers page lists.
 *
 * The page shipped short on 2026-09-07 with a `check` saying the description
 * was still coming; it arrived the same day and this is it, set in the site's
 * section vocabulary the way `careers-growth-engineer.ts` is: the mission on
 * the grey band, the success measures as a list, the responsibilities and the
 * offer as full-width rows. The emoji headings of the source document are
 * dropped — the rest of the site sets its section headings as type.
 *
 * Every "Apply now" points at `#contact`, the shared contact band's own anchor
 * at the foot of the document.
 */
export const route: RouteDef = {
  id: 'careersProductEngineer',
  path: 'become-a-digitopian/product-engineer',
  priority: 0.6,
}

export const careersProductEngineer: WrittenPage = {
  navLabel: 'Product Engineer',
  title: 'Product Engineer',
  description:
    'Open role at Digitopia: Product Engineer, hybrid in Istanbul. A full-stack builder with strong product judgment and a bias toward shipping, owning the Impact Platform end to end.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Product Engineer',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'A full-stack builder with strong product judgment and a bias toward shipping. Own meaningful product problems end to end, working close to users and turning complex transformation workflows into simple, dependable experiences.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we enable companies to understand where they stand, decide what matters next, and turn digital and AI ambition into measurable progress.',
        'Our Impact Platform brings measurement, benchmarking, roadmapping, portfolio visibility and AI-powered guidance together in one place. The product is where Digitopia’s consulting know-how becomes repeatable, scalable and useful every day.',
        'To keep raising that bar, we are looking for a Product Engineer: a full-stack builder with strong product judgment and a bias toward shipping. You will own meaningful product problems end to end, working close to users and turning complex transformation workflows into simple, dependable experiences.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro:
        'You will help make the Impact Platform easier to use, more powerful and more scalable. Success means:',
      items: [
        'Shipping product improvements that users adopt and value.',
        'Turning complex enterprise workflows into clear, intuitive experiences.',
        'Owning quality from interface and API design through deployment and monitoring.',
        'Balancing speed with maintainability, security and a coherent product architecture.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’ll do',
      items: [
        {
          title: 'Own features end to end',
          body: 'Take a problem from definition through API design, implementation, release and monitoring in production. You’ll be the person who knows how your feature behaves under real load, not just whether it passed review.',
        },
        {
          title: 'Shape service boundaries',
          body: 'We run a set of focused backend services with explicit contracts between them. You’ll design and evolve those contracts — OpenAPI for external surfaces, gRPC for service-to-service — and decide what belongs where.',
        },
        {
          title: 'Make dense workflows feel simple',
          body: 'Our users work through long, multi-step, data-heavy processes. The craft here is turning that into interfaces people complete without training and trust the output of.',
        },
        {
          title: 'Build real-time and AI-powered experiences',
          body: 'Streaming interactions over WebSocket, model integration, structured outputs, guardrails, and the feedback loops that make an AI feature reliable rather than impressive-once.',
        },
        {
          title: 'Raise the bar on quality',
          body: 'Tests, observability, code review, and the unglamorous work that closes the gap between “shipped” and “dependable”.',
        },
        {
          title: 'Decide with evidence',
          body: 'Use usage signals and customer conversations to argue for what’s worth building, and be willing to change your mind.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'You bring relevant experience in full-stack, frontend, backend or product engineering. Strong new graduates with meaningful internships, open-source contributions or substantial personal projects are encouraged to apply.',
        'You are comfortable working across more than one layer of a product and can reason about the complete user experience.',
        'You can code in at least one language from our environment, such as Kotlin or Java for backend services, or TypeScript/JavaScript for frontend development.',
        'You understand APIs, databases, software architecture, testing, version control and cloud environments.',
        'You communicate trade-offs clearly and work well with technical and non-technical teammates.',
        'You are comfortable with evolving requirements and can turn ambiguity into steady progress.',
        'You take ownership, seek feedback, and follow through after release.',
        'You are fluent in English. An additional language is a plus.',
        'Show us what you have built: please include links to relevant products, a portfolio, GitHub profile, case studies or demos where available.',
      ],
    },
    {
      kind: 'list',
      heading: 'Technical skills we’re looking for',
      items: [
        'Frontend: TypeScript/JavaScript, modern component-based frameworks, design systems, and accessible responsive interfaces.',
        'Backend and integration: Kotlin, Java, Spring, gRPC, OpenAPI/Swagger, PostgreSQL, and enterprise integrations.',
        'Cloud and delivery: AWS, Docker, Kubernetes, Terraform, observability, and CI/CD.',
        'Product domains: B2B SaaS, analytics, workflow tools, collaboration products, or AI-enabled enterprise software.',
      ],
      body: 'You do not need to match every keyword. We care more about product judgment, engineering fundamentals, learning speed, and your ability to turn a real user problem into a reliable solution.',
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'Product impact from day one',
          body: 'Build features used by organizations to measure, prioritize and manage transformation.',
        },
        {
          title: 'End-to-end ownership',
          body: 'Influence the problem definition, solution design, implementation, release and iteration.',
        },
        {
          title: 'A meaningful domain',
          body: 'Apply product engineering to strategic decisions, organizational change and measurable business impact.',
        },
        {
          title: 'Modern technology',
          body: 'Work across cloud software, enterprise data, analytics and AI-enabled product experiences.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, experimentation time and internal knowledge-sharing.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading:
        'Ready to build a product that turns transformation insight into visible, measurable action?',
      sub: 'Apply now. We would love to meet you if you are ready to think like a product owner, build like an engineer, and keep improving what you ship.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'product-engineer',
      heading: 'Apply for Product Engineer',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
